import { styled, Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MuiDrawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { userPermissionsAtom } from 'atoms/permissions';
import { isDrawerOpen as isDrawerOpenAtom } from 'atoms/drawer';
import { drawerWidth } from 'utils/constants';
import { hasPermissions } from 'utils/permissions';
import { menuItems } from './menuItems';
import NavMenuItem from './NavMenuItem';
import BranchSelector from 'components/Header/BranchSelector';
import LanguageSelector from 'components/Header/LanguageSelector';

const miniWidth = (theme: Theme) => `calc(${theme.spacing(8)} + 1px)`;

const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => {
  const transition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
  });

  return {
    width: open ? drawerWidth : miniWidth(theme),
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition,
    '& .MuiDrawer-paper': {
      width: open ? drawerWidth : miniWidth(theme),
      overflowX: 'hidden',
      transition,
    },
  };
});

const Sidebar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useAtom(isDrawerOpenAtom);
  const permissions = useAtomValue(userPermissionsAtom);
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const close = () => setIsDrawerOpen(false);

  if (isMobile) {
    return (
      <MuiDrawer anchor="left" open={isDrawerOpen} onClose={close} slotProps={{ paper: { sx: { width: '100%' } } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Toolbar disableGutters sx={{ justifyContent: 'flex-end', px: 1 }}>
            <IconButton onClick={close} aria-label={t('common.closeMenu')}>
              <CloseIcon />
            </IconButton>
          </Toolbar>

          <List sx={{ flexGrow: 1 }}>
            {menuItems
              .filter(item => item.showInMobile)
              .filter(item => hasPermissions(permissions, item.requiredPermissions))
              .map(item => (
                <NavMenuItem key={item.id} item={item} onNavigated={close} />
              ))}
          </List>

          <Box
            sx={{
              backgroundColor: '#fff',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <BranchSelector variant="mobile" />
            </Box>
            <LanguageSelector variant="inline" />
          </Box>

          <Stack
            spacing={1}
            gap={2}
            pb={4}
            sx={{
              background: theme.palette.highlightField.main,
              p: 2,
            }}
          >
            <Typography variant="body1">
              {t('header.welcome')} <b>{auth.user!!.profile.name}</b>
            </Typography>
            <Button
              sx={{ background: 'white' }}
              startIcon={<LogoutOutlinedIcon />}
              onClick={() => auth.signoutRedirect({ id_token_hint: auth.user?.id_token })}
              color="inherit"
              fullWidth
            >
              {t('header.logout')}
            </Button>
          </Stack>
        </Box>
      </MuiDrawer>
    );
  }

  return (
    <DesktopDrawer variant="permanent" open={isDrawerOpen}>
      <Toolbar />
      <Box>
        <List>
          {menuItems
            .filter(item => hasPermissions(permissions, item.requiredPermissions))
            .map(item => (
              <NavMenuItem key={item.id} item={item} showTooltip />
            ))}
        </List>
      </Box>
    </DesktopDrawer>
  );
};

export default Sidebar;
